import { useEffect, useState } from 'react'
import { accessSessionIdentity } from '../../../data/accessSessionIdentity'
import type { CfoCurrentStatementMetadata, QuickBooksCfoPresentationResponse, ServerApiClient } from '../../../data/ServerApiClient'

const month = (date: string | null | undefined): string | null => date && /^\d{4}-\d{2}(?:-\d{2})?$/.test(date) ? date.slice(0, 7) : null

export function matchesBriefPresentation(
  payload: QuickBooksCfoPresentationResponse,
  workbookId: string,
  requestedPeriod: string | null,
  source: CfoCurrentStatementMetadata | null,
): boolean {
  if (payload.workbook_id !== workbookId || (requestedPeriod && month(payload.source_state?.reporting_period) !== month(requestedPeriod))) return false
  // An already-open retained Home cannot combine its old numbers with a
  // newly activated native presentation merely because the month agrees.
  if (!source && payload.source_state?.native_source_revision != null) return false
  if (source) {
    const state = payload.source_state
    if (!source.source_identity || !source.configuration_identity || !source.selected_period_end
      || state?.native_source_revision !== source.source_revision || state.native_source_job_id !== source.source_job_id
      || state.native_configuration_identity !== source.configuration_identity
      || state.native_statement_source_identities?.length !== 3
      || state.native_statement_source_identities.some(identity => identity !== source.source_identity)
      || state.native_statement_period_ends?.length !== 3
      || state.native_statement_period_ends.some(end => end !== source.selected_period_end)) return false
  }
  return true
}

/** Optional read enrichment: no refresh admission or fallback reads. A stale
 * promise cannot attach an older reporting selection to the current brief. */
export function useCfoPresentationEnrichment({ appApi, workbookId, requestedPeriod, source, ready }: {
  appApi: Pick<ServerApiClient, 'getQuickBooksCfoPresentation' | 'getToken'>
  workbookId: string | null | undefined
  requestedPeriod: string | null
  source: CfoCurrentStatementMetadata | null
  ready: boolean
}) {
  const [loaded, setLoaded] = useState<{ key: string; payload: QuickBooksCfoPresentationResponse; source: CfoCurrentStatementMetadata | null } | null>(null)
  const sessionIdentity = accessSessionIdentity(appApi.getToken())
  const key = JSON.stringify([workbookId, requestedPeriod, source?.source_revision, source?.source_job_id, source?.source_identity, source?.configuration_identity, source?.selected_period_end, sessionIdentity])
  useEffect(() => {
    setLoaded(null)
    if (!workbookId || !ready || !requestedPeriod) return
    let cancelled = false
    const sessionIsCurrent = () => !cancelled && accessSessionIdentity(appApi.getToken()) === sessionIdentity
    void appApi.getQuickBooksCfoPresentation(workbookId, { includeArAnalysis: true, requestedPeriod })
      .then((payload) => {
        if (sessionIsCurrent() && matchesBriefPresentation(payload, workbookId, requestedPeriod, source)) setLoaded({ key, payload, source })
      })
      .catch(() => { if (sessionIsCurrent()) setLoaded(null) })
    return () => { cancelled = true }
  }, [appApi, workbookId, requestedPeriod, source?.source_revision, source?.source_job_id, ready, source, sessionIdentity, key])
  return ready && loaded?.key === key && loaded.source === source ? loaded.payload : null
}
